import { AdminRole, AdminUser } from './types';

export const PREINSCRIPTION_STATUS_BADGE: Record<string, string> = {
  nouveau: 'bg-gray-100 text-gray-700',
  a_rappeler: 'bg-amber-100 text-amber-800',
  rdv_pris: 'bg-blue-100 text-blue-700',
  rdv_confirme: 'bg-blue-100 text-blue-700',
  rdv_passe: 'bg-indigo-100 text-indigo-700',
  rdv_no_show: 'bg-amber-100 text-amber-700',
  injoignable: 'bg-stone-200 text-stone-600',
  inscrit: 'bg-emerald-100 text-emerald-700',
  perdu: 'bg-red-100 text-red-700',
};

export const STATUS_LABELS: Record<string, string> = {
  nouveau: 'Nouveau',
  a_rappeler: 'À rappeler',
  rdv_pris: 'RDV pris',
  rdv_confirme: 'RDV confirmé',
  rdv_passe: 'RDV passé',
  rdv_no_show: 'RDV manqué',
  injoignable: 'Injoignable',
  inscrit: 'Inscrit',
  perdu: 'Perdu',
};

// Statuses an admin may set from the visites table (excludes derived-only states
// that the CRM flow assigns automatically, but keeps the full funnel available).
export const ADMIN_STATUS_OPTIONS = [
  'nouveau',
  'a_rappeler',
  'rdv_pris',
  'rdv_confirme',
  'rdv_passe',
  'rdv_no_show',
  'injoignable',
  'inscrit',
  'perdu',
] as const;

// Canonical visite levels a téléconseiller can be assigned to.
export const VISITE_NIVEAUX = ['Maternelle', 'Primaire', 'Collège', 'Lycée'] as const;

export const OUTCOME_LABELS: Record<string, string> = {
  rdv_pris: 'RDV pris',
  a_rappeler: 'À rappeler',
  no_answer: 'Pas de réponse',
  pas_interesse: 'Pas intéressé',
};

export const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status;

export const preinscriptionStatusBadgeClass = (status: string): string =>
  PREINSCRIPTION_STATUS_BADGE[status] ?? 'bg-gray-100 text-gray-700';

export function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${preinscriptionStatusBadgeClass(status)}`}>
      {statusLabel(status)}
    </span>
  );
}

export const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat('fr-MA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));

export const formatDate = formatDateTime;

export const formatRdvDate = (value: string) =>
  new Intl.DateTimeFormat('fr-MA', { dateStyle: 'full' }).format(new Date(value));

export const formatShortDateTime = (value: string) =>
  new Intl.DateTimeFormat('fr-MA', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

export function defaultAdminPath(user: Pick<AdminUser, 'role'>): string {
  return user.role === 'TELECONSEILLER' ? '/admin/appels' : '/admin/visites';
}

export function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  const min = Math.round(ms / 60000);
  if (min < 1) return '< 1 min';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  const d = Math.floor(h / 24);
  const hr = h % 24;
  return hr ? `${d}j ${hr}h` : `${d}j`;
}

export function formatPercent(value: number | null): string {
  return value == null ? '—' : `${Math.round(value * 100)}%`;
}

export const roleLabel = (role: AdminRole | null): string =>
  role === 'ADMIN' ? 'Administrateur' : role === 'TELECONSEILLER' ? 'Téléconseiller' : '—';
